import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  MagnifyingGlassIcon,
  CreditCardIcon,
  HeartIcon,
} from '@heroicons/react/24/outline';

/**
 * Three-step explainer between the bookable carousels and the social proof
 * block. No images, no API — purely a static trust-builder.
 *
 *   Choose  →  Book  →  Live the moment
 */
const STEPS = [
  { num: '01', icon: MagnifyingGlassIcon, titleKey: 'home.howItWorks1Title', bodyKey: 'home.howItWorks1Body' },
  { num: '02', icon: CreditCardIcon,       titleKey: 'home.howItWorks2Title', bodyKey: 'home.howItWorks2Body' },
  { num: '03', icon: HeartIcon,            titleKey: 'home.howItWorks3Title', bodyKey: 'home.howItWorks3Body' },
];

function StepCard({ step, t, index }) {
  const Icon = step.icon;
  return (
    <li className="relative flex flex-1 flex-col items-center text-center">
      {/* Numbered icon node */}
      <div className="relative z-10 flex h-20 w-20 items-center justify-center rounded-full bg-white shadow-card ring-1 ring-slate-200/80 transition-all duration-300 group-hover:scale-105">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-50">
          <Icon className="h-7 w-7 text-brand-500" />
        </div>
        {/* numbered badge */}
        <span className="absolute -top-2 -right-2 flex h-7 w-7 items-center justify-center rounded-full bg-accent-500 text-xs font-bold text-brand-900 shadow-card rtl:-left-2 rtl:right-auto">
          {step.num}
        </span>
      </div>

      <h3 className="mt-5 font-display text-lg font-semibold text-ink sm:text-xl">
        {t(step.titleKey)}
      </h3>
      <p className="mt-2 max-w-xs text-sm leading-relaxed text-ink-muted">
        {t(step.bodyKey)}
      </p>
    </li>
  );
}

export default function HowItWorks() {
  const { t } = useTranslation();

  return (
    <section className="relative bg-surface py-16 sm:py-20">
      <div className="mx-auto w-full max-w-container px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <div className="mx-auto max-w-2xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-accent-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-accent-700">
            {t('home.howItWorksEyebrow')}
          </span>
          <h2 className="mt-3 font-display text-[22px] font-semibold tracking-tight text-ink sm:text-3xl lg:text-4xl">
            {t('home.howItWorksTitle')}
          </h2>
          <p className="mt-2 text-sm text-ink-muted sm:text-base">
            {t('home.howItWorksSubtitle')}
          </p>
        </div>

        {/* Steps */}
        <ol className="relative mt-12 flex flex-col items-stretch gap-12 sm:mt-16 sm:flex-row sm:gap-6 lg:gap-10">
          {/* Connecting dashed line — desktop only */}
          <div
            className="absolute inset-x-[16.66%] top-10 -z-0 hidden h-px border-t-2 border-dashed border-slate-200 sm:block"
            aria-hidden="true"
          />

          {STEPS.map((step, i) => (
            <StepCard key={step.num} step={step} t={t} index={i} />
          ))}
        </ol>

        {/* CTA */}
        <div className="mt-12 flex justify-center sm:mt-16">
          <Link
            to="/events"
            className="inline-flex items-center gap-2 rounded-full bg-brand-500 px-6 py-3 text-sm font-semibold text-white shadow-card transition-all duration-200 hover:bg-brand-600 hover:shadow-card-hover"
          >
            {t('home.howItWorksCta')}
            <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 rtl:rotate-180" aria-hidden>
              <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 0 1 .02-1.06L11.168 10 7.23 6.29a.75.75 0 1 1 1.04-1.08l4.5 4.25a.75.75 0 0 1 0 1.08l-4.5 4.25a.75.75 0 0 1-1.06-.02Z" clipRule="evenodd" />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
}
